
import * as React from "react"
import { FieldError, useFormContext } from "react-hook-form"
import { FormFieldContext } from "../context/FormFieldContext"
import { FormItemContext } from "../context/FormItemContext"

// Return type for useFormField hook
interface FormFieldContextReturn {
  id: string;
  name: string;
  formItemId: string;
  formDescriptionId: string;
  formMessageId: string;
  error?: FieldError;
}

// Define our own FieldState interface since it's not directly exported from react-hook-form
interface FieldState {
  isDirty: boolean;
  isTouched: boolean;
  invalid: boolean;
  error?: FieldError;
}

const useFormField = (): FormFieldContextReturn => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  
  // Add safety check: if we're not in a form context, return minimal safe values
  const formContext = useFormContext();
  
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  // Get fieldState safely
  const fieldState = formContext?.getFieldState ? 
    formContext.getFieldState(fieldContext.name, formContext.formState) : 
    {} as FieldState;

  const { id } = itemContext || { id: "" }

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    // Safely access the error property from fieldState
    error: fieldState?.error,
  }
}

export { useFormField }
